import { Field, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useId, useState } from 'react'
import { PatchObjectResponse } from 'shared/types/object.type'
import { useBlocker } from 'site/shared/hooks/useBlocker'
import { normalizeRelationArrayForPatch } from 'site/Renderer/components/ObjectView/lib/normalizeRelationArrayForPatch'
import { useSiteState } from 'siteState/shared'
import useCancelModal from './CancelModal/useCancelModal'

type Callbacks = {
  onSucceed?: () => void
  onCancel?: () => void
}

type UseEditValuesProps = {
  fields: Pick<Field, 'column_json'>[]
  relationFieldColumns?: string[]
  yesObject?: YesObject
  onSave?: (values: Partial<YesObject>) => Promise<PatchObjectResponse | void>
}

export const REDIRECT_CALLBACK_KEY = 'REDIRECT_CALLBACK_KEY'
export const OBJECT_VIEW_NAVIGATION_CALLBACK_KEY = 'OBJECT_VIEW_NAVIGATION'

const pickRelatedFieldValues = (
  fields: Pick<Field, 'column_json'>[],
  yesObject?: YesObject
): Partial<YesObject> | undefined => {
  if (!yesObject) {
    return undefined
  }

  const result: Partial<YesObject> = {}
  for (const field of fields) {
    const key = field.column_json
    if (key in yesObject) {
      Reflect.set(result, key, Reflect.get(yesObject, key))
    }
  }
  return result
}

export default function useEditValues({
  fields,
  relationFieldColumns = [],
  yesObject,
  onSave
}: UseEditValuesProps) {
  const id = useId()

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [fieldValues, setFieldValues] = useState(() =>
    pickRelatedFieldValues(fields, yesObject)
  )
  const [editCallbacks, setEditCallbacks] = useSiteState<
    Record<string, Callbacks>
  >('cancel-modal-callbacks')

  const { register, blocker } = useBlocker()

  useEffect(
    () => register(() => !!Object.keys(editCallbacks).length),
    [editCallbacks, register]
  )

  const { open: openModal } = useCancelModal()

  useEffect(() => {
    if (isSaving || !isEditing) {
      setEditCallbacks(() => ({}))
      return
    }

    if (blocker.state === 'blocked') {
      setEditCallbacks((prevCallbacks) => ({
        ...prevCallbacks,
        [REDIRECT_CALLBACK_KEY]: {
          onSucceed: blocker.proceed,
          onCancel: blocker.reset
        }
      }))

      openModal()
    }
  }, [blocker, isEditing, isSaving, openModal, setEditCallbacks])

  const cancelChanges = useCallback(() => {
    setFieldValues(pickRelatedFieldValues(fields, yesObject))
    setIsEditing(false)
  }, [setFieldValues, setIsEditing, fields, yesObject])

  const addOnSuccessCallback = useCallback(
    (id: string, callback: () => void) => {
      setEditCallbacks((prevCallbacks) => ({
        ...prevCallbacks,
        [id]: { onSucceed: callback }
      }))
    },
    [setEditCallbacks]
  )

  const edit = useCallback(() => {
    if (isSaving) return

    addOnSuccessCallback(id, cancelChanges)

    setIsEditing(true)
  }, [isSaving, addOnSuccessCallback, id, cancelChanges])

  const cancel = useCallback(() => {
    isEditing && openModal(id)
  }, [isEditing, openModal, id])

  const onChange = useCallback(
    (fieldColumn: string, value: YesObject[string]) => {
      setFieldValues((previousFieldValues) => ({
        ...(previousFieldValues ?? {}),
        [fieldColumn]: value
      }))
    },
    []
  )

  const normalizePayloadForSave = useCallback(
    (payload: Partial<YesObject>): Partial<YesObject> => {
      if (!relationFieldColumns.length) {
        return payload
      }

      const next: Partial<YesObject> = { ...payload }
      for (const col of relationFieldColumns) {
        if (!Object.prototype.hasOwnProperty.call(next, col)) {
          continue
        }
        const current = Reflect.get(next, col)
        Reflect.set(next, col, normalizeRelationArrayForPatch(current))
      }
      return next
    },
    [relationFieldColumns]
  )

  const persistChanges = useCallback(async () => {
    setIsSaving(true)

    try {
      if (onSave && fieldValues) {
        await onSave(normalizePayloadForSave(fieldValues))
      }
    } catch (e) {}

    setIsSaving(false)
    setIsEditing(false)
  }, [onSave, fieldValues, normalizePayloadForSave])

  const saveChanges = useCallback(
    async (fieldToSave: string): Promise<PatchObjectResponse | undefined> => {
      if (!fieldValues || !fieldToSave) return undefined

      const objectToSave: Partial<YesObject> = {}
      Reflect.set(
        objectToSave,
        fieldToSave,
        Reflect.get(fieldValues, fieldToSave)
      )

      return (
        (await onSave?.(normalizePayloadForSave(objectToSave))) ?? undefined
      )
    },
    [fieldValues, onSave, normalizePayloadForSave]
  )

  const changeAndSave = useCallback(
    async (
      fieldColumn: string,
      value: YesObject[string]
    ): Promise<PatchObjectResponse | undefined> => {
      const patch: Partial<YesObject> = {}
      Reflect.set(patch, fieldColumn, value)
      const normalizedPatch = normalizePayloadForSave(patch)

      setFieldValues((previousFieldValues) => ({
        ...(previousFieldValues ?? {}),
        ...normalizedPatch
      }))

      return (await onSave?.(normalizedPatch)) ?? undefined
    },
    [onSave, normalizePayloadForSave]
  )

  useEffect(() => {
    const fromServer = pickRelatedFieldValues(fields, yesObject)
    if (isEditing) {
      setFieldValues((prev) => (prev ? { ...fromServer, ...prev } : fromServer))
    } else {
      setFieldValues(fromServer)
    }
  }, [fields, yesObject, isEditing])

  return {
    edit,
    cancel,
    onChange,
    addOnSuccessCallback,
    cancelChanges,
    persistChanges,
    saveChanges,
    changeAndSave,
    fieldValues,
    isEditing
  }
}
